import React, { useState, useEffect, useRef } from 'react';

const TemplateModal = ({ isOpen, categories = [], template, onClose, onSave, isLoading = false }) => {
  const [categoryId, setCategoryId] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState({});
  const [copiedToken, setCopiedToken] = useState('');
  const textareaRef = useRef(null);

  // Placeholders with copy functionality
  const PLACEHOLDERS = [
   
    { token: '{{skills}}', label: 'Job Skills' },
    { token: '{{bidder_name}}', label: 'Bidder Name' },
    { token: '{{job_title}}', label: 'Job Title' },
  ];

  // ============ Initialize Form on Template Change ============
  useEffect(() => {
    if (template) {
      setCategoryId(template.categoryId || '');
      setContent(template.content || '');
      setErrors({});
    } else {
      setCategoryId('');
      setContent('');
      setErrors({});
    }
  }, [template, isOpen]);

  // ============ Validate Form ============
  const validate = () => {
    const newErrors = {};

    if (!categoryId.trim()) {
      newErrors.categoryId = 'Category is required';
    }

    if (!content.trim()) {
      newErrors.content = 'Content is required';
    } else if (content.trim().length < 5) {
      newErrors.content = 'Content must be at least 5 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Insert placeholder and copy to clipboard with feedback
  const handlePlaceholderClick = async (token) => {
    // Insert into textarea
    const el = textareaRef.current;
    if (!el) {
      setContent((prev) => (prev ? prev + token : token));
    } else {
      const start = el.selectionStart ?? content.length;
      const end = el.selectionEnd ?? content.length;
      const before = content.slice(0, start);
      const after = content.slice(end);
      const next = `${before}${token}${after}`;
      setContent(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
    }

    // Copy to clipboard
    try {
      if (navigator?.clipboard) {
        await navigator.clipboard.writeText(token);
      } else {
        const tmp = document.createElement('textarea');
        tmp.value = token;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
      
      // Show "Copied!" feedback
      setCopiedToken(token);
      setTimeout(() => {
        setCopiedToken('');
      }, 2000);
    } catch {
      // ignore
    }
  };

  // ============ Handle Submit ============
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const selectedCategory = categories.find((c) => c.id === categoryId);

    const payload = {
      ...(template?.id && { id: template.id }),
      categoryId,
      categoryName: selectedCategory?.name || 'Uncategorized',
      content: content.trim(),
      skills: 'All skills',
    };

    onSave(payload);
  };

  const handleCancel = () => {
    setCategoryId('');
    setContent('');
    setErrors({});
    setCopiedToken('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop with whitish blur */}
      <div 
        className="absolute inset-0 bg-white/40 backdrop-blur-md"
        onClick={handleCancel}
      />
      
      {/* Modal Card with Hidden Scrollbar */}
      <div className="relative bg-white rounded-2xl border-[1.5px] border-primary-blue shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-hide">
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        {/* Header */}
        <div className="relative px-8 pt-6 pb-5">
          <h2 className="text-xl font-semibold text-gray-900 text-center">
            {template ? 'Edit Template' : 'Add Template'}
          </h2>
          <button
            onClick={handleCancel}
            className="absolute right-6 top-6 hover:opacity-70 transition-opacity"
            disabled={isLoading}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
              <path d="M8 8L16 16M16 8L8 16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="px-8 pb-6">
          {/* Placeholders Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Placeholders
            </label>
            <div className="grid grid-cols-3 gap-2">
              {PLACEHOLDERS.map((placeholder) => (
                <button
                  key={placeholder.token}
                  type="button"
                  onClick={() => handlePlaceholderClick(placeholder.token)}
                  className={`px-3 py-2 bg-blue-50 text-primary-blue text-xs font-medium rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors relative ${
                    copiedToken === placeholder.token ? 'bg-green-50 border-green-300' : ''
                  }`}
                  disabled={isLoading}
                  title={`Click to insert and copy: ${placeholder.token}`}
                >
                  {copiedToken === placeholder.token ? 'Copied!' : placeholder.label}
                </button>
              ))}
            </div>
          </div>

          {/* Template Segment Dropdown */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Template Segment
            </label>
            <div className="relative">
              <select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setErrors({ ...errors, categoryId: '' });
                }}
                className={`w-full px-4 py-3 border-[1.5px] border-primary-blue rounded-lg focus:outline-none focus:ring-primary-blue focus:ring-1 transition-colors appearance-none bg-white text-sm ${
                  errors.categoryId ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              >
                <option value="">Select here</option>
                {Array.isArray(categories) &&
                  categories.length > 0 &&
                  categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name || 'Unnamed Category'}
                    </option>
                  ))}
              </select>
              {/* Custom Dropdown Arrow */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {errors.categoryId && (
              <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>
            )}
            {categories.length === 0 && (
              <p className="text-yellow-600 text-xs mt-1">
                ⚠️ No categories available. Please create categories first.
              </p>
            )}
          </div>

          {/* Content Field */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Content
            </label>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setErrors({ ...errors, content: '' });
              }}
              placeholder="Write here"
              className={`w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:ring-primary-blue focus:ring-1 transition-colors resize-vertical ${
                errors.content ? 'border-red-500' : 'border-gray-300'
              }`}
              rows={4}
              disabled={isLoading}
            />
            {errors.content && (
              <p className="text-red-500 text-xs mt-1">{errors.content}</p>
            )}
          </div>

          {/* Skills Info (Read-Only) */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Skills
            </label>
            <div className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm bg-gray-50 text-gray-600">
              All skills
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This template applies to all skills in this category
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 py-2.5 bg-primary-blue text-white font-semibold rounded-lg hover:bg-primary-blue/70 transition-colors text-base disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || categories.length === 0}
            >
              {isLoading ? 'Saving...' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 py-2.5 bg-white border-[1.5px] border-primary-blue text-primary-blue font-semibold rounded-lg hover:bg-primary-blue hover:text-white transition-colors text-base disabled:opacity-50"
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateModal;
